import { getLocalizedText } from '../hooks/useLocalizedText';
import { PERMISSIONS, PERMISSION_ORDER } from './permissions';

export const ROLES = {
  PLATFORM_SUPER_ADMIN: 'PLATFORM_SUPER_ADMIN',
  OPS_MANAGER: 'OPS_MANAGER',
  FINANCE_OFFICER: 'FINANCE_OFFICER',
  COMPLIANCE_OFFICER: 'COMPLIANCE_OFFICER',
  SUPPORT_AGENT: 'SUPPORT_AGENT',
  READ_ONLY_AUDITOR: 'READ_ONLY_AUDITOR',
};

export const ROLE_PORTALS = {
  ADMIN: 'ADMIN',
  BUYER: 'BUYER',
  SUPPLIER: 'SUPPLIER',
};

/** Display / sort order for portals */
export const ROLE_PORTAL_ORDER = [
  ROLE_PORTALS.ADMIN,
  ROLE_PORTALS.BUYER,
  ROLE_PORTALS.SUPPLIER,
];

export const ROLE_ORDER = [
  ROLES.PLATFORM_SUPER_ADMIN,
  ROLES.OPS_MANAGER,
  ROLES.FINANCE_OFFICER,
  ROLES.COMPLIANCE_OFFICER,
  ROLES.SUPPORT_AGENT,
  ROLES.READ_ONLY_AUDITOR,
];

/** Spec §2 default permission matrix (fallback when API omits permissionCodes) */
export const DEFAULT_ROLE_PERMISSIONS = {
  [ROLES.PLATFORM_SUPER_ADMIN]: PERMISSION_ORDER,
  [ROLES.OPS_MANAGER]: [
    PERMISSIONS.DASHBOARD_READ,
    PERMISSIONS.ORGS_READ,
    PERMISSIONS.ORGS_WRITE,
    PERMISSIONS.ORG_APPROVE,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.CONTRACTS_READ,
    PERMISSIONS.CATALOG_MODERATE,
    PERMISSIONS.DISPUTES_MEDIATE,
    PERMISSIONS.REPORTS_READ,
  ],
  [ROLES.FINANCE_OFFICER]: [
    PERMISSIONS.DASHBOARD_READ,
    PERMISSIONS.ORGS_READ,
    PERMISSIONS.CONTRACTS_READ,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.REFUND_WRITE,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.REPORTS_READ,
  ],
  [ROLES.COMPLIANCE_OFFICER]: [
    PERMISSIONS.DASHBOARD_READ,
    PERMISSIONS.ORGS_READ,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.CONTRACTS_READ,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.REPORTS_READ,
  ],
  [ROLES.SUPPORT_AGENT]: [
    PERMISSIONS.DASHBOARD_READ,
    PERMISSIONS.ORGS_READ,
    PERMISSIONS.ORG_APPROVE,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.DISPUTES_MEDIATE,
    PERMISSIONS.SUPPORT_WRITE,
  ],
  [ROLES.READ_ONLY_AUDITOR]: [
    PERMISSIONS.DASHBOARD_READ,
    PERMISSIONS.ORGS_READ,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.CONTRACTS_READ,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.REPORTS_READ,
  ],
};

/**
 * @param {{ code?: string, name?: { en?: string, ar?: string } | string, roleLabel?: { en?: string, ar?: string } | string }} role
 */
export function getRoleLabel(role, t, locale = 'ar') {
  if (!role) return '';
  const code = typeof role === 'string' ? role : role.code;
  const fromApi = getLocalizedText(
    typeof role === 'string' ? null : (role.name ?? role.roleLabel),
    locale,
  );
  if (fromApi) return fromApi;
  if (code) {
    const key = `roles.codes.${code}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return code || '';
}

/**
 * @param {import('../auth/AuthContext').User | null} user
 */
export function isSuperAdmin(user) {
  return user?.role === ROLES.PLATFORM_SUPER_ADMIN;
}

/**
 * @param {import('../auth/AuthContext').User | null} user
 * @param {string} roleCode
 */
export function canAssignRole(user, roleCode) {
  if (roleCode === ROLES.PLATFORM_SUPER_ADMIN) {
    return isSuperAdmin(user);
  }
  return true;
}

/**
 * @param {Array<{ code?: string, portal?: string }>} roles
 */
export function sortRoles(roles) {
  const orderIndex = new Map(ROLE_ORDER.map((code, index) => [code, index]));
  const portalIndex = new Map(ROLE_PORTAL_ORDER.map((portal, index) => [portal, index]));
  return [...roles].sort((a, b) => {
    const aPortal = portalIndex.get(a.portal) ?? Number.MAX_SAFE_INTEGER;
    const bPortal = portalIndex.get(b.portal) ?? Number.MAX_SAFE_INTEGER;
    if (aPortal !== bPortal) return aPortal - bPortal;
    const aIndex = orderIndex.get(a.code) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = orderIndex.get(b.code) ?? Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return (a.code || '').localeCompare(b.code || '');
  });
}

/**
 * Stable table row key — codes can collide across portals.
 * @param {{ code?: string, portal?: string }} role
 */
export function roleRowKey(role) {
  return `${role?.portal || 'ADMIN'}:${role?.code || ''}`;
}

/**
 * Whether a permission code may be assigned to a role on the given portal.
 * @param {string | undefined} portal
 * @param {string | undefined} permissionCode
 */
export function isPermissionAllowedForPortal(portal, permissionCode) {
  if (!permissionCode) return false;
  const isAdminPerm = permissionCode.startsWith('ADMIN_');
  const effectivePortal = portal || 'ADMIN';
  if (effectivePortal === 'BUYER' || effectivePortal === 'SUPPLIER') return !isAdminPerm;
  return isAdminPerm;
}

/**
 * @param {Array<{ code?: string }>} permissions
 * @param {string | undefined} portal
 */
export function filterPermissionsForPortal(permissions, portal) {
  if (!portal) return permissions;
  return permissions.filter((p) => isPermissionAllowedForPortal(portal, p.code));
}

/**
 * System / seeded roles cannot be deleted. PLATFORM_SUPER_ADMIN permissions are immutable.
 * @param {{ code?: string, systemRole?: boolean }} role
 */
export function isRolePermissionsLocked(role) {
  return role?.code === ROLES.PLATFORM_SUPER_ADMIN;
}

/**
 * @param {{ systemRole?: boolean }} role
 */
export function isSystemRole(role) {
  return role?.systemRole === true;
}

/**
 * Effective permission codes for a role record (API-first, matrix fallback).
 * @param {{ code?: string, permissionCodes?: string[] }} role
 */
export function getRolePermissionCodes(role) {
  if (role?.permissionCodes?.length) return role.permissionCodes;
  if (role?.code === ROLES.PLATFORM_SUPER_ADMIN) return PERMISSION_ORDER;
  return DEFAULT_ROLE_PERMISSIONS[role?.code] || [];
}
